import React from "react";
import { motion } from "framer-motion";
import ResponseCard from "./ResponseCard";
import ResearchProgress from "./ResearchProgress";
import ModelIcon from "./ModelIcon";
import { Sparkles } from "lucide-react";
import "./Dashboard.css";

/**
 * ResponsesGrid - Container for all response cards
 * @param {Array<string>} selectedModels - Array of selected model names
 * @param {Object} responses - Object mapping model names to response data
 * @param {boolean} isLoading - Whether query is loading
 * @param {boolean} disabled - Whether actions should be disabled
 * @param {Function} getResponseHtml - Function to get HTML content for a model
 * @param {Function} getResponsePlain - Function to get plain text for a model
 * @param {Function} onEdit - Callback for Edit/Export (receives model name)
 * @param {Function} onRemove - Callback for Remove (receives model name)
 * @param {Object} responseRefs - Object of refs for each model's content
 * @param {Object} variants - Framer motion variants
 */
export default function ResponsesGrid({
  selectedModels = [],
  responses = {},
  isLoading = false,
  disabled = false,
  getResponseHtml,
  getResponsePlain,
  onEdit,
  onRemove,
  responseRefs = {},
  variants = null
}) {
  const containerVariants = variants?.container || {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const validModels = ["ChatGPT", "Claude", "Gemini", "Perplexity", "Grok"];

  const filteredModels = selectedModels.filter(model => {
    if (!validModels.includes(model)) return false;
    const responseData = responses[model];
    return responseData ? (
      (responseData.text && responseData.text.trim() !== '') ||
      responseData.loading ||
      responseData.error
    ) : true; // Show empty response field for selected models
  });

  // Show the live activity panel once a run is underway or has produced
  // something. Derived from existing props only.
  const hasAnyResult = filteredModels.some(model => {
    const d = responses[model];
    return !!d && (d.error || d.loading || (d.text && d.text.trim() !== ""));
  });
  const showProgress = filteredModels.length > 0 && (isLoading || hasAnyResult);

  // Nothing has been run yet: show an intentional empty state rather than a
  // row of blank placeholder cards.
  if (!isLoading && !hasAnyResult) {
    return (
      <div className="responses-section-wrapper">
        <div className="rw-empty">
          <span className="rw-empty-icon">
            <Sparkles size={22} />
          </span>
          <h3>Ask a legal question to begin</h3>
          <p>
            Your issue runs across every selected model in parallel. Compare the
            answers, then move through comparison and synthesis.
          </p>
          {filteredModels.length > 0 && (
            <span className="rw-empty-models">
              {filteredModels.map((m) => (
                <ModelIcon key={m} model={m} size={14} />
              ))}
              {filteredModels.length} model
              {filteredModels.length === 1 ? "" : "s"} selected
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="responses-section-wrapper">
      {showProgress && (
        <ResearchProgress
          models={filteredModels}
          responses={responses}
          isLoading={isLoading}
        />
      )}

      <motion.div
        className="responses-container"
        variants={containerVariants}
      >
        <motion.div
          className="responses-grid"
          variants={containerVariants}
        >
          {filteredModels.map((model, i) => {
            const responseData = responses[model];
            const htmlContent = getResponseHtml ? getResponseHtml(model) : "";
            const plainText = getResponsePlain ? getResponsePlain(responseData) : "";
            const hasContent = plainText.trim() !== "";

            return (
              <ResponseCard
                key={model}
                modelName={model}
                htmlContent={htmlContent}
                isGridLoading={isLoading}
                isLoading={responseData?.loading || (isLoading && !responseData)}
                hasError={!!responseData?.error}
                errorMessage={responseData?.error || ""}
                disabled={disabled}
                onEdit={() => onEdit && onEdit(model)}
                onRemove={() => onRemove && onRemove(model)}
                contentRef={el => {
                  if (responseRefs) {
                    responseRefs[model] = el;
                  }
                }}
                variants={variants}
                index={i}
              />
            );
          })}
        </motion.div>
      </motion.div>
    </div>
  );
}
